import React, { useState, useEffect, useRef } from 'react';
import { COLORS } from '../App';

const API_BASE = window.location.port === '3000' ? 'http://localhost:4100/api' : window.location.pathname.startsWith('/cmbs') ? '/cmbs/api' : '/api';

const SECTIONS = [
  { key: 'valuation_summary', label: 'Valuation Summary' },
  { key: 'methodology', label: 'Methodology Review' },
  { key: 'comp_sales_analysis', label: 'Comparable Sales Analysis' },
  { key: 'comp_rents_analysis', label: 'Comparable Rents Analysis' },
  { key: 'highest_and_best_use', label: 'Highest and Best Use' },
  { key: 'strengths', label: 'Appraisal Strengths' },
  { key: 'concerns', label: 'Concerns and Risks' },
  { key: 'questions_for_appraiser', label: 'Questions for Appraiser' },
];

const STATUS = { idle: 'idle', generating: 'generating', done: 'done' };

export default function AppraisalReviewModal({ documentId, documentName, token, onClose }) {
  const [sections, setSections] = useState({});
  const [sectionStatus, setSectionStatus] = useState({});
  const [generationStatus, setGenerationStatus] = useState(STATUS.idle);
  const [progress, setProgress] = useState({ current: 0, total: SECTIONS.length });
  const [regenerating, setRegenerating] = useState(null);
  const [exporting, setExporting] = useState(null);
  const [error, setError] = useState(null);
  const esRef = useRef(null);

  useEffect(() => {
    loadExisting();
    return () => { if (esRef.current) esRef.current.close(); };
  }, []);

  const loadExisting = async () => {
    try {
      const res = await fetch(`${API_BASE}/diligence/outputs/${documentId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const rows = await res.json();
      const loaded = {};
      for (const row of rows.filter(r => r.output_type === 'appraisal_review')) {
        loaded[row.section_key] = row.content;
      }
      if (Object.keys(loaded).length > 0) {
        setSections(loaded);
        setGenerationStatus(STATUS.done);
      }
    } catch (err) {
      console.error('Load existing review error:', err);
    }
  };

  const startGeneration = () => {
    setError(null);
    setSections({});
    setSectionStatus({});
    setGenerationStatus(STATUS.generating);
    setProgress({ current: 0, total: SECTIONS.length });

    const url = `${API_BASE}/diligence/review/stream/${documentId}?token=${encodeURIComponent(token)}`;
    const es = new EventSource(url);
    esRef.current = es;

    es.onmessage = (e) => {
      try {
        const data = JSON.parse(e.data);
        if (data.event === 'section_start') {
          setSectionStatus(s => ({ ...s, [data.key]: 'generating' }));
          setProgress(p => ({ ...p, current: data.index }));
        } else if (data.event === 'section_done') {
          setSections(s => ({ ...s, [data.key]: data.content }));
          setSectionStatus(s => ({ ...s, [data.key]: 'done' }));
          setProgress(p => ({ ...p, current: data.index + 1 }));
        } else if (data.event === 'section_error') {
          setSectionStatus(s => ({ ...s, [data.key]: 'error' }));
        } else if (data.event === 'complete') {
          setGenerationStatus(STATUS.done);
          es.close();
        } else if (data.event === 'error') {
          setError(data.error);
          setGenerationStatus(STATUS.idle);
          es.close();
        }
      } catch {}
    };

    es.onerror = () => {
      if (generationStatus === STATUS.generating) {
        setGenerationStatus(STATUS.done);
      }
      es.close();
    };
  };

  const regenerateSection = async (sectionKey) => {
    setRegenerating(sectionKey);
    try {
      const res = await fetch(`${API_BASE}/diligence/review/section/${documentId}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ section_key: sectionKey }),
      });
      const data = await res.json();
      if (data.success) {
        setSections(s => ({ ...s, [sectionKey]: data.content }));
      } else {
        alert(data.error || 'Regeneration failed');
      }
    } catch (err) {
      alert('Regeneration failed: ' + err.message);
    }
    setRegenerating(null);
  };

  const exportReview = async (format) => {
    setExporting(format);
    try {
      const res = await fetch(`${API_BASE}/diligence/review/export/${documentId}/${format}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `appraisal_review.${format}`;
        a.click();
        URL.revokeObjectURL(url);
      } else {
        const err = await res.json();
        alert(err.error || 'Export failed');
      }
    } catch (err) {
      alert('Export failed: ' + err.message);
    }
    setExporting(null);
  };

  const completedCount = SECTIONS.filter(s => sections[s.key]).length;
  const allDone = completedCount === SECTIONS.length;

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 3000,
      background: '#fff', display: 'flex', flexDirection: 'column',
    }}>
      {/* Header */}
      <div style={{
        background: COLORS.navy, padding: '10px 24px',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0,
      }}>
        <div>
          <h3 style={{ fontFamily: 'Georgia, serif', color: COLORS.gold, fontSize: 16, margin: 0 }}>Appraisal Review</h3>
          <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 2 }}>{documentName}</div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {allDone && (
            <>
              <button onClick={() => exportReview('docx')} disabled={!!exporting}
                style={{ padding: '6px 14px', background: '#1D4ED8', color: '#fff', border: 'none', borderRadius: 4, fontSize: 11, cursor: 'pointer', opacity: exporting === 'docx' ? 0.6 : 1 }}>
                {exporting === 'docx' ? '...' : 'Export DOCX'}
              </button>
              <button onClick={() => exportReview('pptx')} disabled={!!exporting}
                style={{ padding: '6px 14px', background: '#7C3AED', color: '#fff', border: 'none', borderRadius: 4, fontSize: 11, cursor: 'pointer', opacity: exporting === 'pptx' ? 0.6 : 1 }}>
                {exporting === 'pptx' ? '...' : 'Export PPTX'}
              </button>
              <button onClick={() => exportReview('pdf')} disabled={!!exporting}
                style={{ padding: '6px 14px', background: '#DC2626', color: '#fff', border: 'none', borderRadius: 4, fontSize: 11, cursor: 'pointer', opacity: exporting === 'pdf' ? 0.6 : 1 }}>
                {exporting === 'pdf' ? '...' : 'Export PDF'}
              </button>
            </>
          )}
          <button onClick={onClose}
            style={{ padding: '6px 14px', background: 'transparent', border: `1px solid ${COLORS.gold}`, color: COLORS.gold, borderRadius: 4, cursor: 'pointer', fontSize: 12 }}>
            Close
          </button>
        </div>
      </div>

      {/* Body */}
      <div style={{ flex: 1, overflow: 'auto', padding: 28, background: '#F3F4F6' }}>
        {/* Generate button / progress */}
        {generationStatus === STATUS.idle && (
          <div style={{ textAlign: 'center', paddingTop: 60 }}>
            {error && (
              <div style={{ color: '#991B1B', fontSize: 13, marginBottom: 16, background: '#FEE2E2', padding: '10px 20px', borderRadius: 6, display: 'inline-block' }}>
                Error: {error}
              </div>
            )}
            <div style={{ fontSize: 13, color: COLORS.textLight, marginBottom: 24, maxWidth: 500, margin: '0 auto 24px' }}>
              Generate an 8-section appraisal review using Claude AI. Covers valuation, methodology, comps, H&B use, strengths, concerns, and key questions.
            </div>
            <button onClick={startGeneration}
              style={{ padding: '12px 32px', background: COLORS.gold, color: COLORS.navy, border: 'none', borderRadius: 6, fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: 'Georgia, serif' }}>
              Generate Review
            </button>
          </div>
        )}

        {generationStatus === STATUS.generating && (
          <div style={{ marginBottom: 24 }}>
            <div style={{ background: '#fff', borderRadius: 8, padding: '14px 20px', border: `1px solid ${COLORS.border}`, display: 'flex', alignItems: 'center', gap: 14 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.navy, marginBottom: 6 }}>
                  Generating review… {progress.current} of {progress.total} sections complete
                </div>
                <div style={{ background: '#E5E7EB', borderRadius: 4, height: 6 }}>
                  <div style={{
                    background: COLORS.gold, borderRadius: 4, height: 6,
                    width: `${(progress.current / progress.total) * 100}%`,
                    transition: 'width 0.3s ease',
                  }} />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Sections */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 860, margin: '0 auto' }}>
          {SECTIONS.map((section, i) => {
            const content = sections[section.key];
            const status = sectionStatus[section.key];
            const isRegen = regenerating === section.key;

            return (
              <div key={section.key} style={{
                background: '#fff', borderRadius: 8,
                border: `1px solid ${content ? COLORS.border : '#E5E7EB'}`,
                opacity: !content && generationStatus === STATUS.idle ? 0.4 : 1,
              }}>
                <div style={{
                  padding: '10px 18px', borderBottom: content ? `1px solid ${COLORS.border}` : 'none',
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{
                      fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 10,
                      background: content ? '#DCFCE7' : status === 'generating' ? '#FEF9C3' : '#F3F4F6',
                      color: content ? '#166534' : status === 'generating' ? '#854D0E' : '#6B7280',
                    }}>
                      {content ? 'Done' : status === 'generating' ? 'Generating…' : `${i + 1} of ${SECTIONS.length}`}
                    </span>
                    <span style={{ fontFamily: 'Georgia, serif', fontSize: 14, fontWeight: 600, color: COLORS.navy }}>
                      {section.label}
                    </span>
                  </div>
                  {content && (
                    <button onClick={() => regenerateSection(section.key)} disabled={isRegen}
                      style={{ padding: '4px 12px', background: '#F0F2F5', border: `1px solid ${COLORS.border}`, borderRadius: 4, fontSize: 11, cursor: 'pointer', color: COLORS.navy, opacity: isRegen ? 0.6 : 1 }}>
                      {isRegen ? 'Regenerating…' : 'Regenerate'}
                    </button>
                  )}
                </div>
                {content && (
                  <div style={{ padding: '14px 18px', fontSize: 13, color: COLORS.text, lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
                    {content}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {allDone && (
          <div style={{ textAlign: 'center', marginTop: 24, paddingBottom: 20 }}>
            <button onClick={startGeneration}
              style={{ padding: '8px 24px', background: '#F0F2F5', border: `1px solid ${COLORS.border}`, borderRadius: 4, fontSize: 12, cursor: 'pointer', color: COLORS.navy }}>
              Regenerate All Sections
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
